/**
 * Data Transformers - Pure functions for converting between API and UI formats
 * No side effects, no API calls, just data transformation
 */

import type { Event, Task, Supplier, Message, Suggestion } from '../types/mockup'

/**
 * Transform raw API task data to UI Task type
 */
export const transformTask = (raw: any): Task => {
  return {
    ...raw,
    startTime: new Date(raw.startTime),
    endTime: new Date(raw.endTime),
    createdAt: raw.createdAt ? new Date(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : undefined,
  }
}

/**
 * Transform array of raw tasks
 */
export const transformTasks = (rawTasks: any[]): Task[] => {
  return rawTasks.map(transformTask)
}

/**
 * Transform raw API supplier data to UI Supplier type
 */
export const transformSupplier = (raw: any): Supplier => {
  return {
    ...raw,
    lastContact: raw.lastContact ? new Date(raw.lastContact) : undefined,
  }
}

/**
 * Transform array of raw suppliers
 */
export const transformSuppliers = (rawSuppliers: any[]): Supplier[] => {
  return rawSuppliers.map(transformSupplier)
}

/**
 * Transform raw API event data to UI Event type
 */
export const transformEvent = (raw: any): Event => {
  const transformed: Event = {
    ...raw,
    date: raw.date ? new Date(raw.date) : undefined,
    startTime: raw.startTime ? new Date(raw.startTime) : undefined,
    endTime: raw.endTime ? new Date(raw.endTime) : undefined,
    createdAt: raw.createdAt ? new Date(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : undefined,
  }

  // Transform nested arrays if present
  if (raw.tasks) {
    transformed.tasks = transformTasks(raw.tasks)
  }

  if (raw.suppliers) {
    transformed.suppliers = transformSuppliers(raw.suppliers)
  }

  return transformed
}

/**
 * Transform raw API message data to UI Message type
 */
export const transformMessage = (raw: any): Message => {
  return {
    ...raw,
    receivedAt: raw.receivedAt ? new Date(raw.receivedAt) : new Date(),
    processedAt: raw.processedAt ? new Date(raw.processedAt) : undefined,
  }
}

/**
 * Transform array of raw messages
 */
export const transformMessages = (rawMessages: any[]): Message[] => {
  return rawMessages.map(transformMessage)
}

/**
 * Transform raw API suggestion data to UI Suggestion type
 */
export const transformSuggestion = (raw: any): Suggestion => {
  return {
    ...raw,
    createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(),
    expiresAt: raw.expiresAt ? new Date(raw.expiresAt) : undefined,
  }
}

/**
 * Transform dates back to ISO strings for API requests
 */
export const toApiFormat = {
  task: (task: Partial<Task>): any => ({
    ...task,
    startTime: task.startTime instanceof Date ? task.startTime.toISOString() : task.startTime,
    endTime: task.endTime instanceof Date ? task.endTime.toISOString() : task.endTime,
    createdAt: task.createdAt instanceof Date ? task.createdAt.toISOString() : task.createdAt,
    updatedAt: task.updatedAt instanceof Date ? task.updatedAt.toISOString() : task.updatedAt,
  }),

  event: (event: Partial<Event>): any => ({
    ...event,
    date: event.date instanceof Date ? event.date.toISOString() : event.date,
    startTime: event.startTime instanceof Date ? event.startTime.toISOString() : event.startTime,
    endTime: event.endTime instanceof Date ? event.endTime.toISOString() : event.endTime,
    createdAt: event.createdAt instanceof Date ? event.createdAt.toISOString() : event.createdAt,
    updatedAt: event.updatedAt instanceof Date ? event.updatedAt.toISOString() : event.updatedAt,
  }),

  message: (message: Partial<Message>): any => ({
    ...message,
    receivedAt: message.receivedAt instanceof Date ? message.receivedAt.toISOString() : message.receivedAt,
    processedAt: message.processedAt instanceof Date ? message.processedAt.toISOString() : message.processedAt,
  }),
}